import { CSS, type Transform } from '@dnd-kit/utilities'

/**
 * OrderX renders the desktop trade area inside CSS `zoom` (DASHBOARD_DESKTOP_VISUAL_SCALE).
 * Under zoom, DOMRects and pointer coordinates are in the visually scaled space, while
 * layout values (`offsetWidth`, `scrollLeft`) and CSS transforms on descendants are in the
 * element's unscaled local space. These helpers convert between the two.
 */

/**
 * The effective CSS zoom applied to `el` (rendered px per local CSS px); 1 when unzoomed.
 * Prefers the browser's `currentCSSZoom`; otherwise measures the element's rendered vs.
 * layout width, which an own `transform` (e.g. a drag's scale) would skew — so pass an
 * element without one, such as the dragged item's parent.
 */
export function getRenderedScale(el: Element | null | undefined): number {
  if (!el) return 1
  const zoom = (el as Element & { currentCSSZoom?: number }).currentCSSZoom
  if (typeof zoom === 'number' && zoom > 0) return zoom
  const layoutWidth = (el as HTMLElement).offsetWidth
  if (!layoutWidth) return 1
  const scale = el.getBoundingClientRect().width / layoutWidth
  return Number.isFinite(scale) && scale > 0 ? scale : 1
}

/**
 * A dnd-kit transform (measured from viewport rects / pointer movement) converted to the
 * local CSS px a `transform` on `el` is rendered in, so the item tracks the cursor under
 * zoom. Only the rendered transform is converted: collision detection keeps comparing
 * viewport rects with each other, which is already consistent.
 */
export function toLocalTransform<T extends { x: number; y: number }>(
  transform: T | null,
  el: Element | null | undefined
): T | null {
  if (!transform) return null
  const scale = getRenderedScale(el)
  if (scale === 1) return transform
  return { ...transform, x: transform.x / scale, y: transform.y / scale }
}

/**
 * `CSS.Translate.toString` for a sortable/draggable inside the zoomed trade area: the
 * transform converted to local px first (see toLocalTransform). Measures the node's parent,
 * since the node itself may carry its own drag transform.
 */
export function zoomAwareTranslate(
  transform: Transform | null,
  node: { current: HTMLElement | null }
): string | undefined {
  const el = node.current
  return CSS.Translate.toString(toLocalTransform(transform, el?.parentElement ?? el))
}
